// supervisor-loop-types.ts
//
// Types of one control-loop tick, split out of supervisor-loop.ts (bước 0a,
// phase 03 — trần dòng B20). Pure type declarations + one leaf helper
// (`manifestPathsForTicket`) that only those types need — no runtime import,
// so this file cannot create an import cycle with supervisor-loop.ts.
import type { GitRunner } from "./commit-owner.js";
import type { ContractErrorBreaker } from "./contract-error-circuit-breaker.js";
import type { FeatureDispatchGateBlockCode } from "./feature-dispatch-gate-guard.js";
import type { G2PlanReadiness } from "./g2-plan-readiness.js";
import type { MissionStore } from "./mission-store.js";
import type { PolicyAdapterInput, PolicyVerdict } from "./policy-adapter.js";
import type { QuotaConfig } from "./quota-sleep-scheduler.js";
import type { RetryConfig } from "./retry-policy.js";
import type { RuntimeLimits } from "./runtime-limits-from-effective-config.js";
import type { SchedulerProjection } from "./scheduler.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import type { SessionRunnerDeps } from "./session-runner.js";
import type { RouteOutcomeResult } from "./supervisor-outcome-router.js";

export interface SupervisorTickInput {
  store: MissionStore;
  controlRoot: string;
  missionId: string;
  missionWorkspace: string;
  repoDir: string;
  projection: SchedulerProjection;
  /** H4: set by supervisor-run-until-idle.ts when it evaluated G2 this tick; the guard holds features until the plan is ready. */
  g2PlanReadiness?: G2PlanReadiness;
  retryConfig: RetryConfig;
  quotaConfig: QuotaConfig;
  runtimeLimitsForKind: (kind: string) => RuntimeLimits;
  sessionDeps: SessionRunnerDeps;
  gitRunner: GitRunner;
  evaluatePolicy: (input: PolicyAdapterInput) => Promise<PolicyVerdict>;
  buildPrompt: (ticket: Ticket) => string;
  now?: () => Date;
  beforeSleep?: () => Promise<void>;
  /** D03/N24: config's `worker_model`, threaded to the attempt cycle's `--model` arg. */
  workerModel?: string;
  /** Phase 03 (R-15, D14): operator-facing progress line (header/▶/■) — stdout via `log.info`, undefined ⇒ silent (unit tests). */
  progress?: (line: string) => void;
  /** Operator-facing warning line (rejected directives, DAG policy rules 3-4) — stderr via `log.error`; undefined ⇒ silent (unit tests). Threaded down to the outcome router's done path. */
  notify?: (line: string) => void;
  /** Phase 04 (R-23, D11): 1 instance per `run` — undefined ⇒ runSupervisorUntilIdle creates one itself (loopInput). Recorded in finalizeAttemptAfterRoute, shared by both dispatch paths. */
  contractErrorBreaker?: ContractErrorBreaker;
}

export type SupervisorTickResult =
  | { kind: "idle" }
  | { kind: "attempted"; ticketId: string; route: RouteOutcomeResult }
  // Bug I (P15): the next ready ticket is a feature ticket but the mission's
  // G0/G1 authority is not granted (or gates.json is unreadable). Distinct
  // from `idle` — the DAG is NOT exhausted, so the caller must stop rather
  // than treat it as "nothing left to do".
  | {
      kind: "gate_blocked";
      ticketId: string;
      code: FeatureDispatchGateBlockCode;
      reason: string;
    };

export function manifestPathsForTicket(ticket: Ticket): string[] {
  // The ticket's OWN declared scope is the exact manifest commit-owner
  // stages — never "whatever changed", so a worker that touched something
  // outside allowed_paths cannot smuggle it into the commit even if the
  // verifier's scope check somehow passed.
  return [...ticket.allowed_paths];
}
